import { useSearch as useTanstackSearch, type ValidateUseSearchResult } from '@tanstack/react-router';

/**
 * `TFallback` where `T` is `any`: TanStack's route types are `any` without a registered router, as under a package's
 * own tsc.
 */
export type OrFallback<T, TFallback> = 0 extends 1 & T ? TFallback : T;

/** The search of a component that unrelated routes render: every key any route declares, each optional. */
export type LooseSearch = OrFallback<ValidateUseSearchResult<{ strict: false }>, Record<string, string | undefined>>;

/**
 * One query-string value for a component that isn't below one page, such as a shared package's table. A page
 * gets its values as props from its route file. In the app, lint rejects this hook outside route files, which read
 * with `Route.useSearch({ select })`, apart from the exceptions in documentation/routing-reference.md.
 *
 * - `search` holds every key any route declares: declare a key on each route the component reads it on. A
 *   `searchParam` key is typed as its value, but above the route that declares it the key holds the URL's string.
 * - Re-renders only when what `select` returns changes (`===`), so a table writing its filters to the URL doesn't
 *   re-render it: return a value, not a new object or array.
 * - Reads the match of the route rendering the component, so the page being left keeps its values while the next
 *   one loads.
 * - Only a parser over keys known at runtime (app slots) selects the whole `stringifySearch(search)`, since that gets
 *   around declaring each key.
 * - A repeated key keeps only its last value.
 */
export function useSearch<TSelected>({
    select,
}: {
    strict: false;
    select: (search: LooseSearch) => TSelected;
}): TSelected {
    // Cast through `unknown`: TanStack's options type asks whether the selection is JSON (for structural sharing),
    // which it can't decide for a type parameter.
    const selected: unknown = useTanstackSearch({
        strict: false,
        select: (search): unknown => select(search as LooseSearch),
    });
    return selected as TSelected;
}
